import {
    BusinessDescriptionStatus,
    BusinessStatus,
    Role,
    SourceType,
} from '../../mappers/business/toBusinessModel';
import type { BusinessMemberStatus, ImportFormat } from '../../types';

export type ImportSource<TSource> = {
    source: TSource;
    url?: string;
    import_format?: ImportFormat;
};

type BusinessLinksDto = Partial<{
    site: string;
    short_site: string;
    site_display: string;
    telegram: string;
}>;

type BusinessLinksModel = Partial<{
    site: string;
    shortSite: string;
    siteDisplay: string;
    telegram: string;
}>;

export interface BusinessStatItemDto {
    value: number;
    display: string;
    diff_value: number;
    diff_display: string;
}

export interface BusinessStatsDto {
    coverage: {
        total: BusinessStatItemDto;
        subscribers: BusinessStatItemDto;
        notsubscribers: BusinessStatItemDto;
    };
    involved: {
        total: BusinessStatItemDto;
        subscribers: BusinessStatItemDto;
        notsubscribers: BusinessStatItemDto;
    };
    shows: {
        total: BusinessStatItemDto;
        subscribers: BusinessStatItemDto;
        notsubscribers: BusinessStatItemDto;
    };
    shows_in_grid: {
        total: BusinessStatItemDto;
        subscribers: BusinessStatItemDto;
        notsubscribers: BusinessStatItemDto;
    };
    subscribers: {
        total: BusinessStatItemDto;
        subscribed: BusinessStatItemDto;
        unsubscribed: BusinessStatItemDto;
    };
    interactions: {
        total: BusinessStatItemDto;
        clicks: BusinessStatItemDto;
        likes: BusinessStatItemDto;
        shares: BusinessStatItemDto;
        saves: BusinessStatItemDto;
    };
}

export interface BusinessStatItemModel {
    value: number;
    display: string;
    diffValue: number;
    diffDisplay: string;
}

export interface BusinessStatsModel {
    coverage: {
        total: BusinessStatItemModel;
        subscribers: BusinessStatItemModel;
        notsubscribers: BusinessStatItemModel;
    };
    involved: {
        total: BusinessStatItemModel;
        subscribers: BusinessStatItemModel;
        notsubscribers: BusinessStatItemModel;
    };
    shows: {
        total: BusinessStatItemModel;
        subscribers: BusinessStatItemModel;
        notsubscribers: BusinessStatItemModel;
    };
    showsInGrid: {
        total: BusinessStatItemModel;
        subscribers: BusinessStatItemModel;
        notsubscribers: BusinessStatItemModel;
    };
    subscribers: {
        total: BusinessStatItemModel;
        subscribed: BusinessStatItemModel;
        unsubscribed: BusinessStatItemModel;
    };
    interactions: {
        total: BusinessStatItemModel;
        clicks: BusinessStatItemModel;
        likes: BusinessStatItemModel;
        shares: BusinessStatItemModel;
        saves: BusinessStatItemModel;
    };
}

export interface CategoryDto {
    id: number;
    display_name: string;
    children?: CategoryDto[];
}

export enum BusinessType {
    BUSINESS = 'business',
    BLOGGER = 'blogger',
}

export interface BusinessDto {
    profile: {
        id: string;
        public_id: string;
        display_name: string;
        description?: string;
        description_status?: BusinessDescriptionStatus;
        avatar_url?: string;
        cover_url?: string;
        links: BusinessLinksDto;
        posts_count: number;
        created_at: string;
        subscribers_amount: number;
        owners?: {
            logins: string[];
        };
        hide_in_feed?: boolean;
        offer_agreement: OfferAgreement;
        import_settings?: {
            sources: ImportSource<SourceType>[];
        };
        status: BusinessStatus;
        status_display: string;
        display_categories: CategoryDto[];
        show_products_import_settings: boolean;
        use_products_auto_import: boolean;
        disabled_offer_sources?: number[];
        site_url?: string;
        short_site?: string;
        catalog_info?: {
            catalog_id: string;
            button_title: string;
        };
        type?: BusinessType;
        share_link?: string;
        allowed_to_boosts?: boolean;
        review_info?: {
            rating?: number;
            review_count?: number;
            review_url?: string;
        };
    };
    role: Role;
    subscribed?: boolean;
}

export interface BusinessLiteDto {
    id: string;
    url: string;
    public_id: string;
    avatar_url?: string;
    display_name: string;
    description?: string;
    description_status?: BusinessDescriptionStatus;
    subscribed?: boolean;
    role: Role;
    site_url?: string;
    short_site?: string;
    catalog_info?: {
        catalog_id: string;
        button_title: string;
    };
    type?: BusinessType;
    share_link?: string;
    allowed_to_boosts?: boolean;
}

export interface BusinessPersistenceDto {
    id?: string;
    public_id?: string;
    display_name?: string;
    description?: string;
    description_status?: BusinessDescriptionStatus;
    avatar_url?: string;
    cover_url?: string;
    links?: BusinessLinksDto;
    created_at?: string;
    hide_in_feed?: boolean;
    offer_agreement?: OfferAgreement;
    import_settings?: {
        sources: ImportSource<SourceType>[];
    };
    display_categories: CategoryDto[];
    use_products_auto_import?: boolean;
    disabled_offer_sources?: number[];
    share_link?: string;
    allowed_to_boosts?: boolean;
}

interface SourceModel {
    url?: string;
    importFormat?: ImportFormat;
}

export interface CategoryModel {
    id: number;
    name: string;
    children?: CategoryModel[];
}

export interface BusinessModel {
    __type: 'lite' | 'standard';
    url: string;
    statisticUrl: string;
    shareUrl: string;
    id: string;
    publicId: string;
    displayName: string;
    avatarUrl?: string;
    coverUrl?: string;
    role: Role;
    subscribed: boolean;
    description?: string;
    descriptionStatus?: BusinessDescriptionStatus;
    links: BusinessLinksModel;
    createdAt?: string;
    subscribers?: number;
    posts?: number;
    editLink: string;
    hideInFeed?: boolean;
    offerAgreement?: OfferAgreement;
    instagram?: SourceModel;
    telegram?: SourceModel;
    ads?: SourceModel;
    status?: BusinessStatus;
    statusDisplay?: string;
    categories?: Record<number, CategoryModel>;
    showProductsImportSettings?: boolean;
    useProductsAutoImport?: boolean;
    useProductsDisableAutoParser?: boolean;
    siteUrl?: string;
    shortSite?: string;
    catalogTitle: string;
    catalogUrl?: string;
    type?: BusinessType;
    allowedToBoosts?: boolean;
    reviewsInfo?: {
        rating?: number;
        reviews?: number;
        url?: string;
    };
}

interface ImportSourceModel {
    url?: string;
    importFormat?: ImportFormat;
}

export interface BusinessPersistenceModel {
    id?: string;
    publicId?: string;
    displayName?: string;
    description?: string;
    descriptionStatus?: BusinessDescriptionStatus;
    avatarUrl?: string;
    coverUrl?: string;
    links?: BusinessLinksModel;
    createdAt?: string;
    hideInFeed?: boolean;
    offerAgreement?: OfferAgreement;
    instagram?: ImportSourceModel;
    telegram?: ImportSourceModel;
    ads?: ImportSourceModel;
    categories?: Record<number, CategoryModel>;
    useProductsAutoImport?: boolean;
    useProductsDisableAutoParser?: boolean;
    allowedToBoosts?: boolean;
}

export type BusinessDataModel = BusinessModel;

export interface BusinessMemberDto {
    login: string;
    avatar_url: string;
    member_id: string;
    display_name: string;
    is_current_user: boolean;
    status: BusinessMemberStatus;
}

export interface BusinessMemberModel {
    login: string;
    avatarUrl: string;
    memberId: string;
    displayName: string;
    isCurrentUser: boolean;
    status: BusinessMemberStatus;
}

export interface RegistrationModel {
    instagramUrl: string;
    email: string;
    offerAgreement: boolean;
    sendAdMails: boolean;
    telegramUrl?: string;
    vkUrl?: string;
    youtubeUrl?: string;
    otherUrl?: string;
    needCpaRegistration?: boolean;
}

export interface BusinessRegistrationModel extends RegistrationModel {
    domain: string;
    displayName: string;
}

export interface BloggerRegistrationModel extends RegistrationModel {
    bloggersRegisterUrl?: string;
}

export interface RegistrationDto {
    email: string;
    send_ad_mails: boolean;
    offer_agreement: boolean;
    instagram_url?: string;
    telegram_url?: string;
    vk_url?: string;
    youtube_url?: string;
    other_url?: string;
    need_cpa_registration?: boolean;
}

export interface BusinessRegistrationDto extends RegistrationDto {
    domain: string;
    display_name: string;
}

export interface BloggerRegistrationDto extends RegistrationDto {
    bloggers_register_url?: string;
}

export enum OfferAgreement {
    UNSET = 'unset',
    SIGNED = 'signed',
}
